import { authService } from '@pages/Auth/authService';

const handleTokenRefresh = async (): Promise<boolean> => {
  try {
    const refreshToken = authService.getStoredRefreshToken();
    if (!refreshToken) {
      return false;
    }

    const response = await authService.refreshToken(refreshToken);
    if (response.access) {
      localStorage.setItem('access_token', response.access);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Get CSRF token from cookies or fetch from backend
const getCSRFToken = async (): Promise<string | null> => {
  // First try to get from cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }

  // Backend doesn't have /csrf-token/ endpoint, skip fetching

  return null;
};

const makeRequest = async (
  url: string,
  options: RequestInit,
  retryCount = 0
): Promise<Response> => {
  // Add CSRF token for write operations
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method || '')) {
    const csrfToken = await getCSRFToken();
    if (csrfToken && options.headers) {
      (options.headers as Record<string, string>)['X-CSRFToken'] = csrfToken;
    }
  }

  const response = await fetch(url, options);

  // If token expired and we haven't retried yet, try to refresh and retry
  if (response.status === 401 && retryCount === 0) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      // Update headers with new token
      const newHeaders = authService.getAuthHeaders();
      const newOptions = {
        ...options,
        headers: newHeaders,
      };

      // Retry the request with new token
      return makeRequest(url, newOptions, retryCount + 1);
    }
  }

  return response;
};

export const getApiBaseUrl = () => {
  return (
    (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
    'http://localhost:8000/api'
  );
};

const API_BASE_URL = getApiBaseUrl();

export class ApiClient {
  static async get(endpoint: string): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    return makeRequest(url, {
      method: 'GET',
      headers: authService.getAuthHeaders(),
    });
  }

  static async post(endpoint: string, data?: unknown): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    return makeRequest(url, {
      method: 'POST',
      headers: authService.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async put(endpoint: string, data?: unknown): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    return makeRequest(url, {
      method: 'PUT',
      headers: authService.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async patch(endpoint: string, data?: unknown): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    return makeRequest(url, {
      method: 'PATCH',
      headers: authService.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  static async delete(endpoint: string): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    return makeRequest(url, {
      method: 'DELETE',
      headers: authService.getAuthHeaders(),
    });
  }

  static async postFormData(endpoint: string, formData: FormData): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = authService.getAuthHeaders();
    // Remove Content-Type header to let browser set it with boundary for FormData
    delete headers['Content-Type'];

    return makeRequest(url, {
      method: 'POST',
      headers,
      body: formData,
    });
  }
}
